import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Pencil, Check, X as XIcon, ShieldAlert, Archive, Globe2,
  Factory, FileText, Link2, Users2, MapPin, Building2, Mail, CreditCard, Briefcase,
  ImagePlus, ExternalLink,
} from "lucide-react";
import { getAdminAuthHeaders } from "./AdminAuthContext";
import { translateAdminError } from "./adminErrors";
import { StatusBadge } from "./StatusBadge";
import { priceLabel, durationLabel, listingsLabel } from "./planConstants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRY_NAMES_ES = { ES: "España", UY: "Uruguay", US: "Estados Unidos", AR: "Argentina", BR: "Brasil" };
const COUNTRY_CODES = Object.keys(COUNTRY_NAMES_ES);
const ACCESS_LABELS = { trial: "Trial", manual: "Acceso manual", none: "Sin plan asignado" };

function InfoRow({ icon: Icon, label, value, editing, children }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 mb-0.5">{label}</p>
        {editing ? children : <p className="text-sm text-slate-800 break-words">{value || "—"}</p>}
      </div>
    </div>
  );
}

const editFormFrom = (d) => ({
  name: d.name || "", website: d.website || "", industry: d.industry || "", size: d.size || "",
  country_code: d.country_code || "", admin1_name: d.admin1_name || "", admin2_name: d.admin2_name || "",
  city: d.city || "", location: d.location || "", description: d.description || "",
});

const ROLE_LABELS = { owner: "Propietario", admin: "Administrador", recruiter: "Reclutador" };
const ROLE_STYLES = {
  owner: "bg-purple-100 text-purple-700",
  admin: "bg-blue-100 text-blue-700",
  recruiter: "bg-slate-100 text-slate-600",
};

function RoleBadge({ role }) {
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${ROLE_STYLES[role] || ROLE_STYLES.recruiter}`}>{ROLE_LABELS[role] || role}</span>;
}

const errMsg = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  return detail ? translateAdminError(detail) : fallback;
};

export default function AdminCompanyDetailPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmReactivate, setConfirmReactivate] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [companyAccess, setCompanyAccess] = useState(null);
  const [accessHistory, setAccessHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [publicPlans, setPublicPlans] = useState([]);
  const [assignMode, setAssignMode] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [quickType, setQuickType] = useState("trial");
  const [quickDurationMode, setQuickDurationMode] = useState("preset");
  const [quickDays, setQuickDays] = useState(14);
  const [quickStartDate, setQuickStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [overrideEndDate, setOverrideEndDate] = useState(false);
  const [quickEndDateManual, setQuickEndDateManual] = useState("");
  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);
  const [logoEditOpen, setLogoEditOpen] = useState(false);
  const [logoUrlDraft, setLogoUrlDraft] = useState("");
  const [members, setMembers] = useState([]);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("recruiter");
  const [removeTarget, setRemoveTarget] = useState(null);
  const [transferTarget, setTransferTarget] = useState(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/companies/${companyId}`, { headers: getAdminAuthHeaders() });
      setDetail(res.data);
      setForm(editFormFrom(res.data));
      setMembers(res.data.members || []);
      const [accessRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/companies/${companyId}/access`, { headers: getAdminAuthHeaders() }),
        axios.get(`${API}/admin/plans`, { headers: getAdminAuthHeaders(), params: { scope: "public", status: "active", limit: 100 } }),
      ]);
      setCompanyAccess(accessRes.data.current);
      setAccessHistory(accessRes.data.history || []);
      setPublicPlans(plansRes.data.plans || []);
      hasLoadedRef.current = true;
    } catch (err) {
      toast.error("No se pudo cargar la ficha de la empresa");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (searchParams.get("archive") === "1" && detail) {
      setConfirmArchive(true);
      searchParams.delete("archive");
      setSearchParams(searchParams, { replace: true });
    }
  }, [detail, searchParams, setSearchParams]);

  const startEdit = () => setEditing(true);
  const cancelEdit = () => { setEditing(false); setForm(editFormFrom(detail)); };
  const saveEdit = async () => {
    try {
      await axios.put(`${API}/admin/companies/${companyId}`, form, { headers: getAdminAuthHeaders() });
      setDetail((d) => ({ ...d, ...form }));
      setEditing(false);
      toast.success("Datos de la empresa actualizados");
    } catch (err) {
      toast.error(errMsg(err, "No se pudo actualizar la empresa"));
    }
  };

  const suspendCompany = async () => {
    setStatusLoading(true);
    try {
      await axios.post(`${API}/admin/companies/${companyId}/suspend`, {}, { headers: getAdminAuthHeaders() });
      setDetail((d) => ({ ...d, status: "suspended" }));
      toast.success(`${detail.name} suspendida`);
    } catch (err) {
      toast.error(errMsg(err, "No se pudo suspender la empresa"));
    } finally {
      setStatusLoading(false);
    }
  };

  // Reactivación (desde "archived" o "suspended") — solo cambia status a "active".
  // No reabre empleos cerrados/archivados, no toca Plan/Acceso ni company_members.
  const confirmReactivateAction = async () => {
    setReactivating(true);
    try {
      await axios.post(`${API}/admin/companies/${companyId}/activate`, {}, { headers: getAdminAuthHeaders() });
      setDetail((d) => ({ ...d, status: "active" }));
      toast.success(`${detail.name} fue reactivada`);
      setConfirmReactivate(false);
    } catch (err) {
      toast.error(errMsg(err, "No se pudo reactivar la empresa"));
    } finally {
      setReactivating(false);
    }
  };

  const confirmArchiveAction = async () => {
    setArchiving(true);
    try {
      await axios.post(`${API}/admin/companies/${companyId}/archive`, {}, { headers: getAdminAuthHeaders() });
      toast.success(`${detail.name} fue archivada`);
      setConfirmArchive(false);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo archivar la empresa"));
    } finally {
      setArchiving(false);
    }
  };

  const assignExistingPlan = async () => {
    if (!selectedPlanId) { toast.error("Elegí un plan para asignar"); return; }
    try {
      await axios.post(`${API}/admin/companies/${companyId}/access/assign-plan`, { plan_id: selectedPlanId }, { headers: getAdminAuthHeaders() });
      toast.success("Plan asignado correctamente");
      setAssignMode(null);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo asignar el plan"));
    }
  };

  const grantQuickAccess = async () => {
    try {
      await axios.post(`${API}/admin/companies/${companyId}/access/quick`, {
        access_type: quickType, start_date: quickStartDate, duration_mode: quickDurationMode,
        days: quickDurationMode !== "unlimited" ? quickDays : undefined,
        end_date_override: overrideEndDate && quickEndDateManual ? quickEndDateManual : undefined,
      }, { headers: getAdminAuthHeaders() });
      toast.success(quickType === "trial" ? "Trial otorgado" : "Acceso manual otorgado");
      setAssignMode(null);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo otorgar el acceso"));
    }
  };

  const goCreatePrivatePlan = () => {
    navigate(`/admin/plans/new?company_id=${companyId}&company_name=${encodeURIComponent(detail.name)}`);
  };

  const confirmRevokeAccess = async () => {
    try {
      await axios.post(`${API}/admin/companies/${companyId}/access/revoke`, {}, { headers: getAdminAuthHeaders() });
      toast.success("Plan / acceso revocado");
      setRevokeConfirmOpen(false);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo revocar el acceso"));
      setRevokeConfirmOpen(false);
    }
  };

  const saveLogo = async () => {
    try {
      await axios.put(`${API}/admin/companies/${companyId}`, { logo_url: logoUrlDraft }, { headers: getAdminAuthHeaders() });
      setDetail((d) => ({ ...d, logo_url: logoUrlDraft }));
      setLogoEditOpen(false);
      toast.success("Logo actualizado");
    } catch (err) {
      toast.error(errMsg(err, "No se pudo actualizar el logo"));
    }
  };

  // Gestión de usuarios asociados (multi-usuario por empresa) — endpoints reales.
  const addMember = async () => {
    if (!newMemberEmail.trim()) { toast.error("Ingresá un email"); return; }
    try {
      await axios.post(`${API}/admin/companies/${companyId}/members`, { email: newMemberEmail.trim(), role: newMemberRole }, { headers: getAdminAuthHeaders() });
      toast.success("Usuario agregado a la empresa");
      setNewMemberEmail(""); setNewMemberRole("recruiter"); setAddMemberOpen(false);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo agregar el usuario"));
    }
  };

  const changeMemberRole = async (member, role) => {
    try {
      await axios.put(`${API}/admin/companies/${companyId}/members/${member.user_id}`, { role }, { headers: getAdminAuthHeaders() });
      toast.success(`Rol de ${member.name} actualizado a ${ROLE_LABELS[role]}`);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo cambiar el rol"));
    }
  };

  const toggleMemberStatus = async (member) => {
    try {
      await axios.post(`${API}/admin/companies/${companyId}/members/${member.user_id}/toggle-status`, {}, { headers: getAdminAuthHeaders() });
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo actualizar el estado del usuario"));
    }
  };

  const requestRemoveMember = (member) => {
    if (member.role === "owner") { toast.error("Transferí la propiedad antes de quitar al propietario"); return; }
    setRemoveTarget(member);
  };

  const confirmRemoveMember = async () => {
    try {
      await axios.delete(`${API}/admin/companies/${companyId}/members/${removeTarget.user_id}`, { headers: getAdminAuthHeaders() });
      toast.success(`${removeTarget.name} fue desvinculado de la empresa (su cuenta global no se elimina)`);
      setRemoveTarget(null);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo quitar al usuario"));
      setRemoveTarget(null);
    }
  };

  const confirmTransferOwnership = async () => {
    try {
      await axios.post(`${API}/admin/companies/${companyId}/transfer-ownership`, { new_owner_user_id: transferTarget.user_id }, { headers: getAdminAuthHeaders() });
      toast.success(`${transferTarget.name} ahora es el propietario de la empresa`);
      setTransferTarget(null);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo transferir la propiedad"));
      setTransferTarget(null);
    }
  };

  if (loading || !detail) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="admin-company-detail-loading">
        <Loader2 className="h-6 w-6 text-[#028CF8] animate-spin" />
      </div>
    );
  }

  const activeJobs = (detail.jobs || []).filter((j) => j.status === "active").length;
  const associatedUsers = members.length;
  const owner = members.find((m) => m.role === "owner");

  return (
    <div data-testid="admin-company-detail-page" className="max-w-4xl">
      <button data-testid="admin-company-back-button" onClick={() => navigate("/admin/companies")} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft className="h-4 w-4" /> Volver a Empresas
      </button>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="relative shrink-0">
              <div className="h-14 w-14 rounded-xl bg-[#028CF8]/10 flex items-center justify-center font-poppins font-bold text-[#028CF8] text-xl overflow-hidden">
                {detail.logo_url ? <img src={detail.logo_url} alt={detail.name} className="h-full w-full object-cover" /> : detail.name?.[0]?.toUpperCase()}
              </div>
              <button
                data-testid="admin-company-logo-change"
                onClick={() => { setLogoUrlDraft(detail.logo_url || ""); setLogoEditOpen((v) => !v); }}
                className="absolute -bottom-1.5 -right-1.5 h-6 w-6 rounded-full bg-slate-900 text-white flex items-center justify-center border-2 border-white hover:bg-slate-700"
                aria-label="Cambiar logo"
              >
                <ImagePlus className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="min-w-0">
              <h1 data-testid="admin-company-detail-name" className="font-poppins text-xl font-bold text-slate-900 truncate">{detail.name}</h1>
              <div className="flex items-center gap-2 mt-1.5">
                <StatusBadge value={detail.status} testId="admin-company-detail-status" />
                <StatusBadge value={detail.access_type} testId="admin-company-detail-access" />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid="admin-company-header-edit" variant="outline" size="sm" onClick={startEdit}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
            </Button>
            {detail.status === "active" && (
              <Button
                data-testid="admin-company-header-toggle-status"
                variant="outline" size="sm"
                disabled={statusLoading}
                onClick={suspendCompany}
                className="text-amber-600 border-amber-200"
              >
                <ShieldAlert className="h-3.5 w-3.5 mr-1.5" /> Suspender
              </Button>
            )}
            {(detail.status === "suspended" || detail.status === "archived") && (
              <Button
                data-testid="admin-company-header-reactivate"
                variant="outline" size="sm"
                onClick={() => setConfirmReactivate(true)}
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              >
                <ShieldAlert className="h-3.5 w-3.5 mr-1.5" /> Reactivar empresa
              </Button>
            )}
            {detail.status !== "archived" && (
              <Button data-testid="admin-company-header-archive" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmArchive(true)}>
                <Archive className="h-3.5 w-3.5 mr-1.5" /> Archivar / Eliminar
              </Button>
            )}
          </div>
        </div>
        {logoEditOpen && (
          <div data-testid="admin-company-logo-form" className="mt-4 p-4 rounded-lg border border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2 border-2 border-dashed border-slate-300 rounded-lg p-4 mb-3 text-slate-400 text-sm justify-center">
              <ImagePlus className="h-4 w-4" /> Arrastrá una imagen o pegá una URL abajo (carga de archivos disponible al implementar el backend)
            </div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">URL del logo</label>
            <Input data-testid="admin-company-logo-url-input" value={logoUrlDraft} onChange={(e) => setLogoUrlDraft(e.target.value)} placeholder="https://" className="mb-3" />
            <div className="flex items-center gap-2">
              <Button data-testid="admin-company-logo-save" size="sm" onClick={saveLogo} className="bg-[#028CF8] hover:bg-[#0270C5]">
                <Check className="h-3.5 w-3.5 mr-1.5" /> Guardar logo
              </Button>
              <Button data-testid="admin-company-logo-cancel" size="sm" variant="outline" onClick={() => setLogoEditOpen(false)}>Cancelar</Button>
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue={searchParams.get("tab") === "access" ? "access" : "info"} className="w-full">
        <TabsList data-testid="admin-company-tabs" className="mb-4 bg-slate-100">
          <TabsTrigger data-testid="admin-company-tab-info" value="info">Info general</TabsTrigger>
          <TabsTrigger data-testid="admin-company-tab-jobs" value="jobs">Empleos ({(detail.jobs || []).length})</TabsTrigger>
          <TabsTrigger data-testid="admin-company-tab-users" value="users">Usuarios asociados</TabsTrigger>
          <TabsTrigger data-testid="admin-company-tab-access" value="access">Plan / Acceso</TabsTrigger>
        </TabsList>

        {/* ── Info general ── */}
        <TabsContent value="info" data-testid="admin-company-tab-content-info">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">Perfil de la empresa</h3>
              {!editing ? (
                <button data-testid="admin-company-edit-toggle" onClick={startEdit} className="flex items-center gap-1.5 text-xs font-semibold text-[#028CF8] hover:underline">
                  <Pencil className="h-3.5 w-3.5" /> Editar
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <button data-testid="admin-company-edit-cancel" onClick={cancelEdit} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:underline">
                    <XIcon className="h-3.5 w-3.5" /> Cancelar
                  </button>
                  <button data-testid="admin-company-edit-save" onClick={saveEdit} className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline">
                    <Check className="h-3.5 w-3.5" /> Guardar
                  </button>
                </div>
              )}
            </div>

            <InfoRow icon={Building2} label="Nombre" value={detail.name} editing={editing}>
              <Input data-testid="admin-company-edit-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="h-8 text-sm" />
            </InfoRow>
            <InfoRow icon={Factory} label="Industria" value={detail.industry} editing={editing}>
              <Input data-testid="admin-company-edit-industry" value={form.industry} onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))} className="h-8 text-sm" />
            </InfoRow>
            <InfoRow icon={FileText} label="Descripción" value={detail.description} editing={editing}>
              <Textarea data-testid="admin-company-edit-description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="text-sm min-h-[70px]" />
            </InfoRow>
            <InfoRow icon={Link2} label="Sitio web" value={detail.website} editing={editing}>
              <Input data-testid="admin-company-edit-website" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} className="h-8 text-sm" />
            </InfoRow>
            <InfoRow icon={Users2} label="Tamaño del equipo" value={detail.size} editing={editing}>
              <Input data-testid="admin-company-edit-size" value={form.size} onChange={(e) => setForm((f) => ({ ...f, size: e.target.value }))} className="h-8 text-sm" placeholder="Ej: 11-50 empleados" />
            </InfoRow>
            <InfoRow icon={Globe2} label="País" value={COUNTRY_NAMES_ES[detail.country_code] || detail.country_code} editing={editing}>
              <select data-testid="admin-company-edit-country" value={form.country_code} onChange={(e) => setForm((f) => ({ ...f, country_code: e.target.value }))} className="w-full h-8 px-2 rounded-md border border-slate-200 text-sm bg-white">
                <option value="">Sin especificar</option>
                {COUNTRY_CODES.map((cc) => <option key={cc} value={cc}>{COUNTRY_NAMES_ES[cc]}</option>)}
              </select>
            </InfoRow>
            <InfoRow icon={MapPin} label="Región / Estado / Departamento" value={detail.admin1_name} editing={editing}>
              <Input data-testid="admin-company-edit-admin1" value={form.admin1_name} onChange={(e) => setForm((f) => ({ ...f, admin1_name: e.target.value }))} className="h-8 text-sm" />
            </InfoRow>
            <InfoRow icon={MapPin} label="Provincia" value={detail.admin2_name} editing={editing}>
              <Input data-testid="admin-company-edit-admin2" value={form.admin2_name} onChange={(e) => setForm((f) => ({ ...f, admin2_name: e.target.value }))} className="h-8 text-sm" placeholder="Cuando corresponda" />
            </InfoRow>
            <InfoRow icon={MapPin} label="Ciudad / Localidad" value={detail.city} editing={editing}>
              <Input data-testid="admin-company-edit-city" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className="h-8 text-sm" />
            </InfoRow>
            <InfoRow icon={MapPin} label="Ubicación visible" value={detail.location} editing={editing}>
              <Input data-testid="admin-company-edit-location" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="h-8 text-sm" placeholder="Ej: Montevideo, Uruguay" />
            </InfoRow>
          </div>
        </TabsContent>

        {/* ── Empleos ── */}
        <TabsContent value="jobs" data-testid="admin-company-tab-content-jobs">
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]" data-testid="admin-company-jobs-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Título</th>
                  <th className="text-left px-4 py-3">Ubicación</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-left px-4 py-3">Publicado</th>
                  <th className="text-left px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {(detail.jobs || []).map((j) => (
                  <tr key={j.job_id} data-testid={`admin-company-job-row-${j.job_id}`} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-900">{j.title}</td>
                    <td className="px-4 py-3 text-slate-600">{j.location || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge value={j.status} /></td>
                    <td className="px-4 py-3 text-slate-500">{j.posted_date ? new Date(j.posted_date).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">
                      <button data-testid={`admin-company-job-view-${j.job_id}`} onClick={() => navigate(`/admin/jobs?company_id=${detail.company_id}`)} className="flex items-center gap-1 text-xs font-semibold text-[#028CF8] hover:underline">
                        <ExternalLink className="h-3 w-3" /> Ver
                      </button>
                    </td>
                  </tr>
                ))}
                {(detail.jobs || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center py-10 text-slate-400 text-sm">Sin empleos publicados</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ── Usuarios asociados ── */}
        <TabsContent value="users" data-testid="admin-company-tab-content-users">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">Usuarios de la empresa ({members.length})</h3>
              {!addMemberOpen && (
                <button data-testid="admin-company-add-member-toggle" onClick={() => setAddMemberOpen(true)} className="flex items-center gap-1.5 text-xs font-semibold text-[#028CF8] hover:underline">
                  <Users2 className="h-3.5 w-3.5" /> + Agregar usuario
                </button>
              )}
            </div>

            {addMemberOpen && (
              <div data-testid="admin-company-add-member-form" className="mb-5 p-4 rounded-lg border border-slate-200 bg-slate-50 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Email del usuario</label>
                  <Input data-testid="admin-company-add-member-email" value={newMemberEmail} onChange={(e) => setNewMemberEmail(e.target.value)} placeholder="nombre@empresa.com" className="bg-white" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Rol</label>
                  <select data-testid="admin-company-add-member-role" value={newMemberRole} onChange={(e) => setNewMemberRole(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="admin">Administrador</option>
                    <option value="recruiter">Reclutador</option>
                  </select>
                </div>
                <Button data-testid="admin-company-add-member-submit" size="sm" onClick={addMember} className="bg-[#028CF8] hover:bg-[#0270C5]">Agregar</Button>
                <Button data-testid="admin-company-add-member-cancel" size="sm" variant="outline" onClick={() => { setAddMemberOpen(false); setNewMemberEmail(""); }}>Cancelar</Button>
              </div>
            )}

            <div className="space-y-2">
              {members.map((m) => (
                <div key={m.user_id} data-testid={`admin-company-member-row-${m.user_id}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 flex-wrap">
                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center font-semibold text-slate-600 shrink-0">
                    {m.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 truncate">{m.name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 truncate" title={m.email}><Mail className="h-3 w-3" /> {m.email}</p>
                  </div>
                  <RoleBadge role={m.role} />
                  <StatusBadge value={m.status} testId={`admin-company-member-status-${m.user_id}`} />
                  <div className="flex items-center gap-2 flex-wrap">
                    {m.role !== "owner" && (
                      <select
                        data-testid={`admin-company-member-role-select-${m.user_id}`}
                        value={m.role}
                        onChange={(e) => changeMemberRole(m, e.target.value)}
                        className="h-8 px-2 rounded-md border border-slate-200 text-xs bg-white"
                      >
                        <option value="admin">Administrador</option>
                        <option value="recruiter">Reclutador</option>
                      </select>
                    )}
                    {m.role === "owner" ? (
                      <span data-testid={`admin-company-member-owner-tag-${m.user_id}`} className="text-xs text-slate-300" title="Usá 'Hacer propietario' en otro usuario para transferir la propiedad">
                        Propietario actual
                      </span>
                    ) : (
                      <>
                        <button data-testid={`admin-company-member-toggle-${m.user_id}`} onClick={() => toggleMemberStatus(m)} className={`text-xs font-semibold hover:underline ${m.status === "active" ? "text-amber-600" : "text-emerald-600"}`}>
                          {m.status === "active" ? "Desactivar" : "Activar"}
                        </button>
                        <button data-testid={`admin-company-member-transfer-${m.user_id}`} onClick={() => setTransferTarget(m)} className="text-xs font-semibold text-purple-600 hover:underline">
                          Hacer propietario
                        </button>
                        <button data-testid={`admin-company-member-remove-${m.user_id}`} onClick={() => requestRemoveMember(m)} className="text-xs font-semibold text-red-600 hover:underline">
                          Quitar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {members.length === 0 && <p className="text-sm text-slate-500">Sin usuarios asociados</p>}
            </div>
            <p className="text-xs text-slate-500 mt-4">Quitar a un usuario lo desvincula de esta empresa; su cuenta de InfoCityJobs no se elimina. Los roles de empresa (Propietario, Administrador, Reclutador) son independientes de los roles internos del equipo de InfoCityJobs.</p>
          </div>
        </TabsContent>

        {/* ── Plan / Acceso ── */}
        <TabsContent value="access" data-testid="admin-company-tab-content-access">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">Acceso actual</h3>
              {companyAccess && (
                <button data-testid="admin-company-access-revoke-toggle" onClick={() => setRevokeConfirmOpen(true)} className="text-xs font-semibold text-red-600 hover:underline">
                  Revocar acceso
                </button>
              )}
            </div>

            {companyAccess ? (
              <div className="grid sm:grid-cols-2 gap-3 mb-2" data-testid="admin-company-access-current">
                <div className="p-3 rounded-lg border border-slate-100">
                  <p className="text-xs text-slate-400 mb-1">Plan / acceso</p>
                  <p className="text-sm font-semibold text-slate-800" data-testid="admin-company-access-current-name">{companyAccess.plan_name}</p>
                </div>
                <div className="p-3 rounded-lg border border-slate-100">
                  <p className="text-xs text-slate-400 mb-1">Origen</p>
                  <span data-testid="admin-company-access-current-badge" className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                    companyAccess.source === "trial" ? "bg-blue-100 text-blue-700" : companyAccess.source === "manual" ? "bg-purple-100 text-purple-700" : companyAccess.scope === "private" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"
                  }`}>
                    {companyAccess.source === "trial" ? "Trial" : companyAccess.source === "manual" ? "Acceso manual" : companyAccess.scope === "private" ? "Plan privado" : "Plan público"}
                  </span>
                </div>
                <div className="p-3 rounded-lg border border-slate-100">
                  <p className="text-xs text-slate-400 mb-1">Fecha de asignación</p>
                  <p className="text-sm text-slate-700" data-testid="admin-company-access-start">{new Date(companyAccess.assigned_at).toLocaleDateString()}</p>
                </div>
                <div className="p-3 rounded-lg border border-slate-100">
                  <p className="text-xs text-slate-400 mb-1">Vencimiento</p>
                  <p className="text-sm text-slate-700" data-testid="admin-company-access-end-value">{companyAccess.expires_at ? new Date(companyAccess.expires_at).toLocaleDateString() : "Sin vencimiento"}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 mb-2" data-testid="admin-company-access-none">Sin plan asignado</p>
            )}

            <div className="flex flex-wrap gap-2 mt-5 mb-2">
              <button data-testid="admin-company-assign-existing-toggle" onClick={() => setAssignMode(assignMode === "existing" ? null : "existing")}
                className={`h-9 px-4 rounded-lg text-xs font-semibold border ${assignMode === "existing" ? "bg-[#028CF8] text-white border-[#028CF8]" : "border-slate-200 text-slate-600 bg-white"}`}>
                Asignar plan público
              </button>
              <button data-testid="admin-company-create-private-plan" onClick={goCreatePrivatePlan} className="h-9 px-4 rounded-lg text-xs font-semibold border border-purple-200 text-purple-700 bg-white hover:bg-purple-50">
                + Crear plan privado
              </button>
              <button data-testid="admin-company-assign-quick-toggle" onClick={() => setAssignMode(assignMode === "quick" ? null : "quick")}
                className={`h-9 px-4 rounded-lg text-xs font-semibold border ${assignMode === "quick" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 bg-white"}`}>
                Otorgar trial / acceso manual
              </button>
            </div>

            {assignMode === "existing" && (
              <div data-testid="admin-company-assign-existing-form" className="mt-2 p-4 rounded-lg border border-slate-200 bg-slate-50">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Plan público disponible</label>
                <select data-testid="admin-company-assign-existing-select" value={selectedPlanId} onChange={(e) => setSelectedPlanId(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white mb-3">
                  <option value="">Seleccioná un plan...</option>
                  {publicPlans.map((p) => (
                    <option key={p.plan_id} value={p.plan_id}>{p.name} — {priceLabel(p)} — {durationLabel(p.duration_amount, p.duration_unit)} — {listingsLabel(p.listings_mode, p.listings_amount)}</option>
                  ))}
                </select>
                <div className="flex items-center gap-2">
                  <Button data-testid="admin-company-assign-existing-save" size="sm" onClick={assignExistingPlan} className="bg-[#028CF8] hover:bg-[#0270C5]">
                    <Check className="h-3.5 w-3.5 mr-1.5" /> Asignar plan
                  </Button>
                  <Button data-testid="admin-company-assign-existing-cancel" size="sm" variant="outline" onClick={() => setAssignMode(null)}>Cancelar</Button>
                </div>
              </div>
            )}

            {assignMode === "quick" && (
              <div data-testid="admin-company-assign-quick-form" className="mt-2 p-4 rounded-lg border border-slate-200 bg-slate-50">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Tipo de acceso</label>
                <div className="flex gap-2 mb-4">
                  {["trial", "manual"].map((t) => (
                    <button key={t} data-testid={`admin-company-access-type-${t}`} onClick={() => { setQuickType(t); if (t === "trial" && quickDurationMode === "unlimited") setQuickDurationMode("preset"); }}
                      className={`flex-1 h-9 rounded-lg text-xs font-semibold border ${quickType === t ? "bg-[#028CF8] text-white border-[#028CF8]" : "border-slate-200 text-slate-600 bg-white"}`}>
                      {ACCESS_LABELS[t]}
                    </button>
                  ))}
                </div>

                <label className="text-xs font-semibold text-slate-600 mb-1 block">Fecha de inicio</label>
                <input type="date" data-testid="admin-company-access-start-date" value={quickStartDate} onChange={(e) => setQuickStartDate(e.target.value)}
                  className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white mb-4 w-full sm:w-52" />

                <label className="text-xs font-semibold text-slate-600 mb-1 block">Duración</label>
                <div className="flex gap-2 mb-3 flex-wrap">
                  {[7, 14, 30].map((d) => (
                    <button key={d} data-testid={`admin-company-access-days-${d}`} onClick={() => { setQuickDurationMode("preset"); setQuickDays(d); }}
                      className={`h-9 px-3 rounded-lg text-xs font-semibold border ${quickDurationMode === "preset" && quickDays === d ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 bg-white"}`}>
                      {d} días
                    </button>
                  ))}
                  <button data-testid="admin-company-access-duration-custom" onClick={() => setQuickDurationMode("custom")}
                    className={`h-9 px-3 rounded-lg text-xs font-semibold border ${quickDurationMode === "custom" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 bg-white"}`}>
                    Personalizada
                  </button>
                  {quickType === "manual" && (
                    <button data-testid="admin-company-access-duration-unlimited" onClick={() => setQuickDurationMode("unlimited")}
                      className={`h-9 px-3 rounded-lg text-xs font-semibold border ${quickDurationMode === "unlimited" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 bg-white"}`}>
                      Sin vencimiento
                    </button>
                  )}
                </div>

                {quickDurationMode === "custom" && (
                  <Input data-testid="admin-company-access-days-custom" type="number" min="1" value={quickDays} onChange={(e) => setQuickDays(Number(e.target.value))} placeholder="Cantidad de días" className="h-9 w-40 mb-4" />
                )}

                {quickDurationMode !== "unlimited" ? (
                  <div className="mb-4">
                    <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                      <p className="text-xs text-slate-500" data-testid="admin-company-access-end-preview">
                        Vencimiento: {overrideEndDate && quickEndDateManual ? new Date(quickEndDateManual).toLocaleDateString() : new Date(new Date(quickStartDate).getTime() + quickDays * 86400000).toLocaleDateString()}
                      </p>
                      <button data-testid="admin-company-access-end-override-toggle" onClick={() => setOverrideEndDate((v) => !v)} className="text-xs font-semibold text-[#028CF8] hover:underline">
                        {overrideEndDate ? "Usar cálculo automático" : "Definir fecha manualmente"}
                      </button>
                    </div>
                    {overrideEndDate && (
                      <input type="date" data-testid="admin-company-access-end-date-input" value={quickEndDateManual} onChange={(e) => setQuickEndDateManual(e.target.value)}
                        className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white w-full sm:w-52" />
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 mb-4">Este acceso no vence hasta que se revoque manualmente.</p>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <Button data-testid="admin-company-access-save" size="sm" onClick={grantQuickAccess} className="bg-[#028CF8] hover:bg-[#0270C5]">
                    <Check className="h-3.5 w-3.5 mr-1.5" /> Otorgar acceso
                  </Button>
                  <Button data-testid="admin-company-access-cancel" size="sm" variant="outline" onClick={() => setAssignMode(null)}>Cancelar</Button>
                </div>
              </div>
            )}
          </div>

          {accessHistory.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mt-4">
              <button data-testid="admin-company-access-history-toggle" onClick={() => setHistoryOpen((v) => !v)} className="w-full flex items-center justify-between text-left">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">Historial de accesos ({accessHistory.length})</h3>
                <span className="text-xs font-semibold text-[#028CF8]">{historyOpen ? "Ocultar" : "Mostrar"}</span>
              </button>
              {historyOpen && (
                <div className="divide-y divide-slate-100 mt-3" data-testid="admin-company-access-history-list">
                  {accessHistory.map((h) => (
                    <div key={h.access_id} data-testid={`admin-company-access-history-item-${h.access_id}`} className="py-2.5 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{h.plan_name}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(h.assigned_at).toLocaleDateString()} → {h.expires_at ? new Date(h.expires_at).toLocaleDateString() : "sin vencimiento"}
                        </p>
                        {h.revoked_at && <p className="text-xs text-red-500">Revocado el {new Date(h.revoked_at).toLocaleDateString()}{h.revoke_reason ? ` — ${h.revoke_reason}` : ""}</p>}
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${!h.revoked_at ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {!h.revoked_at ? "Vigente" : "Finalizado"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Confirmación al revocar acceso ── */}
      <AlertDialog open={revokeConfirmOpen} onOpenChange={setRevokeConfirmOpen}>
        <AlertDialogContent data-testid="admin-company-access-revoke-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Revocar el acceso de {detail.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              La empresa queda sin plan asignado. Podés volver a asignarle un plan público, uno privado o un acceso rápido en cualquier momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-company-access-revoke-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-company-access-revoke-confirm-button" onClick={confirmRevokeAccess} className="bg-red-600 hover:bg-red-700">Revocar acceso</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Confirmación de reactivación (desde archivada o suspendida) ── */}
      <AlertDialog open={confirmReactivate} onOpenChange={setConfirmReactivate}>
        <AlertDialogContent data-testid="admin-company-reactivate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Reactivar {detail.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              La empresa vuelve a estar activa y visible públicamente. Los empleos que se
              cerraron al archivarla NO se reabren automáticamente — el empleador deberá
              reactivarlos manualmente si corresponde. El plan/acceso y los usuarios
              asociados no se modifican.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-company-reactivate-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-company-reactivate-confirm-button" onClick={confirmReactivateAction} disabled={reactivating} className="bg-emerald-600 hover:bg-emerald-700">
              {reactivating ? "Reactivando..." : "Reactivar empresa"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Confirmación de archivado con dependencias ── */}
      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent data-testid="admin-company-archive-confirm" className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Archivar {detail.name}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-left">
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1.5 text-sm text-slate-700">
                  <p className="flex items-center gap-2"><Briefcase className="h-3.5 w-3.5 text-slate-400" /> {activeJobs} empleo(s) activo(s) se cerrarán automáticamente</p>
                  <p className="flex items-center gap-2"><Users2 className="h-3.5 w-3.5 text-slate-400" /> {associatedUsers} usuario(s) asociado(s) — no se eliminan</p>
                  <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" /> Propietario: {owner ? `${owner.name} (${owner.email})` : "—"}</p>
                  <p className="flex items-center gap-2"><CreditCard className="h-3.5 w-3.5 text-slate-400" /> Plan/acceso vigente: {ACCESS_LABELS[detail.access_type] || "Sin plan asignado"}</p>
                </div>
                <p className="text-slate-600">Al confirmar: la empresa se archiva y deja de ser visible en la plataforma pública. Los empleos, aplicaciones, guardados e historial se conservan. El propietario y los usuarios asociados no se eliminan.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-company-archive-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-company-archive-confirm-button" onClick={confirmArchiveAction} disabled={archiving} className="bg-red-600 hover:bg-red-700">
              {archiving ? "Archivando..." : "Archivar empresa"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Confirmación al quitar un usuario ── */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="admin-company-member-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar a {removeTarget?.name} de {detail.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Se desvincula de esta empresa y pierde acceso a su panel. Su cuenta de InfoCityJobs no se elimina y conserva acceso a cualquier otro uso que tenga en la plataforma.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-company-member-remove-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-company-member-remove-confirm-button" onClick={confirmRemoveMember} className="bg-red-600 hover:bg-red-700">
              Quitar usuario
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Confirmación de transferencia de propiedad ── */}
      <AlertDialog open={!!transferTarget} onOpenChange={(o) => !o && setTransferTarget(null)}>
        <AlertDialogContent data-testid="admin-company-transfer-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Transferir la propiedad de {detail.name} a {transferTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {owner?.name} deja de ser propietario y pasa a rol Administrador. {transferTarget?.name} se convierte en el nuevo propietario de la empresa. Esta acción queda registrada en auditoría.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-company-transfer-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-company-transfer-confirm-button" onClick={confirmTransferOwnership} className="bg-purple-600 hover:bg-purple-700">
              Transferir propiedad
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
